import type { ComponentType } from 'react';
import { InstagramIcon, KickIcon, TelegramIcon, TiktokIcon, XIcon, YouTubeIcon } from './icons';

export interface SocialTile {
  platform: string;
  url: string;
  label: string;
  Icon: ComponentType;
  /** The platform's own tile colour, as in the reference. */
  background: string;
  foreground: string;
}

/**
 * How each platform is drawn, and in what order.
 *
 * The order is the reference's, not the API's: Kick first because it is where
 * the streaming happens. The API returns whatever the owner saved, so the order
 * has to be imposed here rather than assumed.
 *
 * A platform this build does not know is DROPPED rather than drawn blank. The
 * server can be ahead of a deployed bundle, and an unlabelled grey square that
 * links somewhere is worse than one fewer icon.
 */
const tiles: Record<string, Omit<SocialTile, 'platform' | 'url'>> = {
  kick: { label: 'Kick', Icon: KickIcon, background: '#53fc18', foreground: '#0b0b0b' },
  x: { label: 'X', Icon: XIcon, background: '#0b0b0b', foreground: '#ffffff' },
  instagram: {
    label: 'Instagram',
    Icon: InstagramIcon,
    background: 'linear-gradient(45deg, #f9ce34, #ee2a7b 45%, #6228d7)',
    foreground: '#ffffff',
  },
  youtube: { label: 'YouTube', Icon: YouTubeIcon, background: '#ff0033', foreground: '#ffffff' },
  telegram: { label: 'Telegram', Icon: TelegramIcon, background: '#2aabee', foreground: '#ffffff' },
  tiktok: { label: 'TikTok', Icon: TiktokIcon, background: '#0b0b0b', foreground: '#ffffff' },
};

const order = ['kick', 'x', 'instagram', 'youtube', 'telegram', 'tiktok'];

export function socialsFor(socials: readonly { platform: string; url: string }[]): SocialTile[] {
  return socials
    .flatMap((social) => {
      const tile = tiles[social.platform];

      return tile ? [{ platform: social.platform, url: social.url, ...tile }] : [];
    })
    .sort((a, b) => order.indexOf(a.platform) - order.indexOf(b.platform));
}
